import { useState } from 'react';
import { CheckCircle2, XCircle, Clock, Loader2, Download, X, ChevronDown, ChevronUp, AlertCircle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import type { ImportJob, ImportError } from '@/types/import';
import { ImportStatus } from '@/types/import';
import { importService } from '@/services/import-service';

interface ImportProgressCardProps {
  job: ImportJob;
  onCancel?: (jobId: string) => void;
  onComplete?: () => void;
  onDelete?: (jobId: string) => void;
}

export function ImportProgressCard({ job, onCancel, onComplete, onDelete }: ImportProgressCardProps): React.ReactElement {
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [isErrorsOpen, setIsErrorsOpen] = useState(false);
  const [loadingErrors, setLoadingErrors] = useState(false);
  const [showAllErrors, setShowAllErrors] = useState(false);

  // Use the job prop directly - parent hook handles polling
  const currentJob = job;

  // For failed imports, show 0% progress and use successCount for display
  const progress = currentJob.status === ImportStatus.Failed 
    ? 0 
    : currentJob.totalRows > 0 
      ? (currentJob.processedRows / currentJob.totalRows) * 100 
      : 0;
  
  // Show successful imports count, not total processed
  const displayCount = currentJob.successCount;

  const getStatusPill = () => {
    switch (currentJob.status) {
      case ImportStatus.Completed:
        return (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900/20">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
            <span className="text-xs font-medium text-green-700 dark:text-green-300">Completed</span>
          </div>
        );
      case ImportStatus.Failed:
        return (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-100 dark:bg-red-900/20">
            <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
            <span className="text-xs font-medium text-red-700 dark:text-red-300">Failed</span>
          </div>
        );
      case ImportStatus.Processing:
        return (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/20">
            <Loader2 className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 animate-spin" />
            <span className="text-xs font-medium text-blue-700 dark:text-blue-300">Processing</span>
          </div>
        );
      case ImportStatus.Pending:
        return (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-100 dark:bg-yellow-900/20">
            <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
            <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300">Pending</span>
          </div>
        );
      case ImportStatus.Cancelled:
        return (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-900/20">
            <X className="h-4 w-4 text-gray-600 dark:text-gray-400 flex-shrink-0" />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Cancelled</span>
          </div>
        );
      default:
        return null;
    }
  };

  const handleDownloadErrors = async () => {
    try {
      const blob = await importService.downloadErrorReport(currentJob.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `import-errors-${currentJob.id}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download error report:', error);
    }
  };

  const loadErrors = async () => {
    if (errors.length > 0 || loadingErrors) return;
    
    setLoadingErrors(true);
    try {
      const summary = await importService.getSummary(currentJob.id);
      const mappedErrors = (summary.errors || []).map(e => ({
        row: e.rowNumber,
        field: e.field,
        error: e.errorMessage,
        value: e.rowData || ''
      }));
      setErrors(mappedErrors);
    } catch (error) {
      console.error('Failed to load errors:', error);
      setErrors([]);
    } finally {
      setLoadingErrors(false);
    }
  };

  const handleErrorsToggle = () => {
    if (!isErrorsOpen && errors.length === 0) {
      loadErrors();
    }
    setIsErrorsOpen(!isErrorsOpen);
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Header with close button in top right */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h4 className="font-semibold text-sm">
                  {currentJob.fileName}
                </h4>
                {getStatusPill()}
              </div>
              <p className="text-xs text-muted-foreground">
                Started {new Date(currentJob.startedDate ?? currentJob.createdDate).toLocaleString()}
              </p>
            </div>
            
            {/* View Accounts and Close/Delete buttons in top right */}
            <div className="flex items-start gap-2">
              {/* View Accounts button - only show for completed imports with successful imports */}
              {currentJob.status === ImportStatus.Completed && currentJob.successCount > 0 && (
                <Link to={`/assets?importJobId=${currentJob.id}&fileName=${encodeURIComponent(currentJob.fileName)}`}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                  >
                    <ExternalLink className="h-3 w-3 mr-1.5" />
                    View Accounts
                  </Button>
                </Link>
              )}
              
              {/* Close/Delete button */}
              {(currentJob.status === ImportStatus.Completed || 
                currentJob.status === ImportStatus.Failed || 
                currentJob.status === ImportStatus.Cancelled) && onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(currentJob.id)}
                  className="h-6 w-6 p-0 hover:bg-destructive/10 -mt-1"
                >
                  <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                </Button>
              )}
            </div>
          </div>

          {/* Progress Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {currentJob.successCount} of {currentJob.totalRows} Assets Imported
              </span>
              <span className="text-sm font-semibold text-muted-foreground">
                {Math.round(progress)}%
              </span>
            </div>
            <Progress 
              value={progress} 
              className={`h-2 ${currentJob.status === ImportStatus.Failed ? '[&>div]:bg-red-600' : ''}`}
            />
          </div>

          {/* Error Details - Collapsible */}
          {currentJob.errorCount > 0 && (
            <div className="pt-1 border-t">
              <Collapsible open={isErrorsOpen} onOpenChange={handleErrorsToggle}>
                <CollapsibleTrigger asChild>
                  <button
                    className="flex items-center justify-between text-sm text-primary hover:text-primary/80 transition-colors w-full pt-[5px]"
                  >
                    <span>View Error Details</span>
                    {isErrorsOpen ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-3">
                  <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-3 space-y-3">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-red-900 dark:text-red-100">
                        {currentJob.errorCount} Import Error{currentJob.errorCount !== 1 ? 's' : ''} Detected
                      </p>
                    </div>
                    
                    {loadingErrors ? (
                      <div className="flex items-center justify-center py-2">
                        <Loader2 className="h-4 w-4 animate-spin text-red-500" />
                        <span className="ml-2 text-xs text-red-700 dark:text-red-300">Loading errors...</span>
                      </div>
                    ) : errors.length > 0 ? (
                      <div className="space-y-2">
                        <ul className="list-disc pl-4 space-y-1 text-xs text-red-800 dark:text-red-200">
                          {(showAllErrors ? errors : errors.slice(0, 20)).map((err, idx) => (
                            <li key={idx}>
                              <span className="font-medium">Row {err.row}</span>
                              {err.field && <span> • Field: {err.field}</span>}
                              <span> • {err.error}</span>
                              {err.value && <span className="text-red-600 dark:text-red-400"> (Value: {err.value})</span>}
                            </li>
                          ))}
                        </ul>
                        {errors.length > 20 && (
                          <button
                            onClick={() => setShowAllErrors(!showAllErrors)}
                            className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:underline"
                          >
                            {showAllErrors ? 'Show less' : `Show all ${errors.length} errors`}
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-red-700 dark:text-red-300">
                        Error details unavailable. Download the error report for full details.
                      </p>
                    )}
                    
                    {currentJob.errorReportUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadErrors}
                        className="w-full border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/20 hover:text-red-800 dark:hover:text-red-200"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download Error Report
                      </Button>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}

          {/* Cancel Action for Processing */}
          {(currentJob.status === ImportStatus.Processing || currentJob.status === ImportStatus.Pending) && onCancel && (
            <div className="pt-1 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onCancel(currentJob.id)}
                className="w-full"
              >
                Cancel Import
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
